import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getFactoringBatchById } from 'src/redux/slices/cheque-factoring';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LoadingScreen } from 'src/components/loading-screen';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import BatchDetails from '../batch-details';

type Props = {
  id: string;
};

export default function BatchDetailView({ id }: Props) {
  const { batch } = useAppSelector((state) => state.chequeFactoring);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getFactoringBatchById({ batchId: id }));
  }, [dispatch, id]);

  if (batch.status === 'loading' || batch.status === 'idle') {
    return <LoadingScreen />;
  }

  if (batch.status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load batch details"
        description={batch.error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.chequeFactoring.batches.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Batches
          </Button>
        }
        sx={{ py: 10 }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Batch Details"
        links={[
          { name: 'Cheque Factoring', href: paths.dashboard.chequeFactoring.batches.root },
          { name: 'Batches', href: paths.dashboard.chequeFactoring.batches.root },
          { name: 'Batch Details' },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <BatchDetails batch={batch.data ?? undefined} />
    </Container>
  );
}
